import type {
  EnrollmentStepSubmissionResponse,
  EnrollmentUserProfile,
  ResumedEnrollment
} from '@/api/enrollment-api'
import { clearEnrollmentStore, useEnrollmentStore } from '@/stores'
import type { EligibilityFormData } from '../../../components/eligibilitySteps/types'
import {
  readEnrollmentDraftBootstrapFields,
  readWidgetLeadBootstrapFields
} from '../../../utils/enrollmentBootstrapSources'
import {
  getEnrollmentDraftLocationExternalId,
  getEnrollmentDraftProgramExternalId,
  getEnrollmentDraftRecord
} from '../../../utils/enrollmentDraftSelectors'
import {
  hydrateEnrollmentForm,
  isoToFormDate,
  normalizeTrainingModeForForm
} from '../../../utils/enrollmentHydration'
import { normalizeStateName } from '../../../components/eligibilitySteps/states'

export const getStoredEmail = () => {
  return readEnrollmentDraftBootstrapFields().email
}

export const getStoredEnrollmentZipCode = () => {
  return readEnrollmentDraftBootstrapFields().zipCode
}

export function getStoredResumeEnrollment() {
  return useEnrollmentStore.getState().resumedEnrollment as
    | ResumedEnrollment
    | EnrollmentStepSubmissionResponse
    | null
}

export function getStoredUserProfile() {
  return useEnrollmentStore.getState().userProfile as EnrollmentUserProfile | null
}

export function getEmptyInitialFormData(): EligibilityFormData {
  return {
    email: '',
    dateOfBirth: '',
    isCitizen: null,
    gender: '',
    ethnicity: '',
    educationLevel: '',
    driverLicenseState: '',
    driverLicenseNumber: '',
    licenseIssueDate: '',
    licenseExpirationDate: '',
    ssn: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    zipCode: '',
    location: '',
    selectedProgram: '',
    transmissionType: '',
    endorsements: [],
    trainingMode: undefined,
    hasLaptop: undefined,
    trainingStartDate: '',
    yardDays: [],
    driverLicenseDocumentFileName: ''
  }
}

export function getWidgetLeadInitialFormData(): EligibilityFormData {
  const widgetLead = readWidgetLeadBootstrapFields()
  const emptyFields = getEmptyInitialFormData()

  return {
    ...emptyFields,
    email: widgetLead.email,
    zipCode: widgetLead.zipCode
  }
}

export function hasActiveEnrollmentSession() {
  return Boolean((useEnrollmentStore.getState().sessionToken?.trim() ?? '') || getStoredResumeEnrollment())
}

export function getInitialFormData(): EligibilityFormData {
  if (!hasActiveEnrollmentSession()) {
    return getWidgetLeadInitialFormData()
  }

  const re = getStoredResumeEnrollment()
  const profile = getStoredUserProfile()
  const storedDraft = getEnrollmentDraftRecord()

  const base: EligibilityFormData = {
    ...getEmptyInitialFormData(),
    email: getStoredEmail() || re?.email || '',
    dateOfBirth: isoToFormDate(re?.dateOfBirth),
    isCitizen: null,
    gender: re?.gender || '',
    ethnicity: re?.ethnicity || '',
    educationLevel: re?.educationLevel || '',
    driverLicenseState: normalizeStateName(re?.driversLicenseState),
    driverLicenseNumber: re?.driversLicenseNumber || '',
    licenseIssueDate: isoToFormDate(re?.driversLicenseIssueDate),
    licenseExpirationDate: isoToFormDate(re?.driversLicenseExpiry),
    ssn: profile?.personalData?.ssnLastFour ? `xxx-xx-${profile?.personalData?.ssnLastFour}` : '',
    emergencyContactName:
      profile?.personalData?.emergencyContactName || re?.emergencyContactName || '',
    emergencyContactPhone:
      profile?.personalData?.emergencyContactPhone || re?.emergencyContactPhone || '',
    zipCode: re?.zipCode || getStoredEnrollmentZipCode(),
    location: re?.trainingLocationExternalId || getEnrollmentDraftLocationExternalId(storedDraft),
    selectedProgram:
      re?.priceListItemId || re?.programExternalId || getEnrollmentDraftProgramExternalId(storedDraft),
    transmissionType: re?.preferredTransmission?.toLowerCase() || '',
    endorsements: re?.endorsements ?? [],
    trainingMode: normalizeTrainingModeForForm(re?.trainingMode),
    hasLaptop: re?.hasLaptop,
    trainingStartDate: re?.preferredStartDate || '',
    yardDays: re?.yardDays ?? [],
    driverLicenseDocumentFileName: ''
  }

  return hydrateEnrollmentForm({
    profile,
    enrollment: re ?? profile?.currentEnrollment,
    previous: base
  }).formData
}

export const clearAnonymousEnrollmentState = () => {
  clearEnrollmentStore()
}
